"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import Link from "next/link";
import Footer from "@/components/Footer";
import Logo from "@/components/Logo";
import TraitBar from "@/components/TraitBar";
import { describeError } from "@/lib/errorSummary";
import { reportCaughtException } from "@/lib/sentry-lazy";
import type { Locale } from "@/lib/i18n";

// 라우트 에러 바운더리(error.tsx)용 결과 카드 패러디 — 404 와 같은 톤으로
// 에러 화면도 플랫폼 정체성을 유지한다. 보안 주의: 스택·원문 메시지는
// 렌더하지 않는다(describeError 의 안전 요약 + digest 만 노출).
const COPY = {
  ko: {
    chip: "돌발 상황 분석",
    lead: "방금 일어난 일의 유형은",
    title: "예상치 못한 오류",
    traits: [{
      label: "당신 잘못",
      percent: 0
    }, {
      label: "서버 당황도",
      percent: 100
    }, {
      label: "복구 가능성",
      percent: 96
    }],
    hint: "잠시 후 다시 시도해 주세요. 계속 반복되면 알려 주시면 빠르게 확인할게요.",
    digestLabel: "오류 코드",
    adminHint: "관리자는 Cloudflare Workers 로그에서 digest로 스택을 조회하세요.",
    retry: "다시 시도",
    home: "홈으로",
    homeHref: "/",
    support: "문의하기",
    supportHref: "/support?kind=bug"
  },
  en: {
    chip: "Incident analysis",
    lead: "What just happened was",
    title: "An Unexpected Error",
    traits: [{
      label: "Your fault",
      percent: 0
    }, {
      label: "Server panic",
      percent: 100
    }, {
      label: "Recovery odds",
      percent: 96
    }],
    hint: "Please try again in a moment. If it keeps happening, let us know and we will look into it.",
    digestLabel: "Error code",
    adminHint: "Admins: look up the digest in Cloudflare Workers logs.",
    retry: "Try again",
    home: "Home",
    homeHref: "/en",
    support: "Contact us",
    supportHref: "/en/support?kind=bug"
  }
} as const;
export default function ErrorResultCard(t0) {
  const $ = _c(59);
  const {
    locale,
    error,
    reset
  } = t0;
  let t1;
  let t2;
  if ($[0] !== error) {
    t1 = () => {
      reportCaughtException(error);
      console.error(error);
    };
    t2 = [error];
    $[0] = error;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const copy = COPY[locale];
  let t3;
  if ($[3] !== error) {
    t3 = describeError(error);
    $[3] = error;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const summary = t3;
  let t4;
  if ($[5] !== locale) {
    t4 = <Logo size="md" locale={locale} />;
    $[5] = locale;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== copy.chip) {
    t5 = <span className="chip self-center">{copy.chip}</span>;
    $[7] = copy.chip;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== copy.lead) {
    t6 = <p className="text-xs text-slate-500">{copy.lead}</p>;
    $[9] = copy.lead;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== copy.title) {
    t7 = <h1 className="font-display text-2xl text-slate-900">{copy.title}</h1>;
    $[11] = copy.title;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== summary) {
    t8 = <p className="text-sm leading-6 text-slate-600">{summary}</p>;
    $[13] = summary;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== copy.hint) {
    t9 = <p className="text-xs leading-5 text-slate-500">{copy.hint}</p>;
    $[15] = copy.hint;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className="flex flex-col gap-2">{t6}{t7}{t8}{t9}</div>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== copy.traits) {
    t11 = copy.traits.map(_temp);
    $[22] = copy.traits;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11) {
    t12 = <div className="flex flex-col gap-2.5">{t11}</div>;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== copy.digestLabel || $[27] !== error.digest) {
    t13 = error.digest ? <p className="text-xs text-slate-400">{copy.digestLabel}:{" "}<span className="font-mono">{error.digest}</span></p> : null;
    $[26] = copy.digestLabel;
    $[27] = error.digest;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t12 || $[31] !== t13 || $[32] !== t5) {
    t14 = <div className="card flex w-full max-w-sm flex-col gap-5 text-center">{t5}{t10}{t12}{t13}</div>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
    $[32] = t5;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== copy.adminHint) {
    t15 = <p className="text-[11px] text-slate-400">{copy.adminHint}</p>;
    $[34] = copy.adminHint;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== copy.retry || $[37] !== reset) {
    t16 = <button type="button" onClick={reset} className="btn-primary">{copy.retry}</button>;
    $[36] = copy.retry;
    $[37] = reset;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== copy.home || $[40] !== copy.homeHref) {
    t17 = <Link href={copy.homeHref} className="btn-ghost inline-flex items-center justify-center">{copy.home}</Link>;
    $[39] = copy.home;
    $[40] = copy.homeHref;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== copy.support || $[43] !== copy.supportHref) {
    t18 = <Link href={copy.supportHref} className="btn-ghost inline-flex items-center justify-center">{copy.support}</Link>;
    $[42] = copy.support;
    $[43] = copy.supportHref;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = <div className="flex flex-wrap justify-center gap-2">{t16}{t17}{t18}</div>;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t14 || $[50] !== t15 || $[51] !== t19 || $[52] !== t4) {
    t20 = <main className="flex flex-col items-center gap-6 pb-10 pt-8 text-center">{t4}{t14}{t15}{t19}</main>;
    $[49] = t14;
    $[50] = t15;
    $[51] = t19;
    $[52] = t4;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== locale) {
    t21 = <Footer locale={locale} />;
    $[54] = locale;
    $[55] = t21;
  } else {
    t21 = $[55];
  }
  let t22;
  if ($[56] !== t20 || $[57] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  return t22;
}
function _temp(trait) {
  return <TraitBar key={trait.label} label={trait.label} percent={trait.percent} />;
}